import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI, adsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Megaphone, Plus, TrendUp, CurrencyDollar, Eye, MouseSimple, Target,
  Lightning, Warning, CheckCircle, Gauge, Archive, Clock
} from '@phosphor-icons/react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend, PieChart, Pie, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { toast } from 'sonner';
import { FabricatedDataNotice } from '../components/brand/FabricatedDataNotice';

const PLATFORM_COLORS = { Meta: '#1A56DB', Google: '#00D4AA', TikTok: '#EF4444' };
const PIE_COLORS = ['var(--accent)', 'var(--success)', 'var(--error)', 'var(--warning)'];

export default function AdsPage() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [skuReport, setSkuReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', platform: 'Meta', budget: 1000, objective: 'Conversions', audience: '' });
  const [andromeda, setAndromeda] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [approvals, setApprovals] = useState(null);
  const [pmaxAudit, setPmaxAudit] = useState(null);
  const [assetGrps, setAssetGrps] = useState(null);
  const [shopFeed, setShopFeed] = useState(null);
  const [aiMax, setAiMax] = useState(null);
  const [gApprovals, setGApprovals] = useState(null);
  const [qsSom, setQsSom] = useState(null);
  const [activePlatform, setActivePlatform] = useState('meta');

  useEffect(() => {
    clientsAPI.list().then(r => {
      setClients(r.data);
      if (r.data.length > 0) setSelectedClient(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedClient) return;
    setLoading(true);
    Promise.all([
      adsAPI.campaigns(selectedClient),
      adsAPI.performance(selectedClient),
      adsAPI.skuReport(selectedClient),
    ]).then(([c, p, s]) => {
      setCampaigns(c.data);
      setPerformance(p.data);
      setSkuReport(s.data);
    }).catch(() => toast.error('Failed to load ads data'))
      .finally(() => setLoading(false));
    // Meta intelligence data
    adsAPI.andromedaAudit(selectedClient).then(r => setAndromeda(r.data)).catch(() => {});
    adsAPI.creativePortfolio(selectedClient).then(r => setPortfolio(r.data)).catch(() => {});
    adsAPI.aiCitationOverlay(selectedClient).then(r => setOverlay(r.data)).catch(() => {});
    adsAPI.metaApprovals(selectedClient).then(r => setApprovals(r.data)).catch(() => {});
    // Google Ads intelligence
    adsAPI.pmaxAudit(selectedClient).then(r => setPmaxAudit(r.data)).catch(() => {});
    adsAPI.assetGroups(selectedClient).then(r => setAssetGrps(r.data)).catch(() => {});
    adsAPI.shoppingFeed(selectedClient).then(r => setShopFeed(r.data)).catch(() => {});
    adsAPI.aiMaxSearch(selectedClient).then(r => setAiMax(r.data)).catch(() => {});
    adsAPI.googleApprovals(selectedClient).then(r => setGApprovals(r.data)).catch(() => {});
    adsAPI.qsSomCorrelation(selectedClient).then(r => setQsSom(r.data)).catch(() => {});
  }, [selectedClient]);

  const handleCreateCampaign = async () => {
    try {
      await adsAPI.createCampaign({ ...form, client_id: selectedClient });
      toast.success('Campaign created');
      setDialogOpen(false);
      const res = await adsAPI.campaigns(selectedClient);
      setCampaigns(res.data);
    } catch { toast.error('Failed to create campaign'); }
  };

  const toggleCampaignStatus = async (campaign) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    try {
      await adsAPI.updateCampaign(campaign.id, { status: newStatus });
      const res = await adsAPI.campaigns(selectedClient);
      setCampaigns(res.data);
      toast.success(`Campaign ${newStatus}`);
    } catch { toast.error('Failed to update'); }
  };

  const totalSpend = campaigns.reduce((s, c) => s + (c.spend || 0), 0);
  const totalConversions = campaigns.reduce((s, c) => s + (c.conversions || 0), 0);
  const avgRoas = campaigns.length > 0
    ? (campaigns.reduce((s, c) => s + (c.roas || 0), 0) / campaigns.length).toFixed(2)
    : 0;

  const platformData = ['Meta', 'Google', 'TikTok'].map(p => ({
    platform: p,
    spend: campaigns.filter(c => c.platform === p).reduce((s, c) => s + (c.spend || 0), 0),
    conversions: campaigns.filter(c => c.platform === p).reduce((s, c) => s + (c.conversions || 0), 0),
  }));

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ads-page">
        <FabricatedDataNotice reason="Campaign, performance, and SKU-report data below is seeded on first load and then persisted — there is no real Meta, Google, or TikTok Ads account connected in this environment, so ROAS, spend, and conversion numbers are illustrative, not a measurement of real ad performance." />
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">
              Paid Ads
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Campaign management, budget allocation & SKU reporting</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger data-testid="ads-client-select" className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">{c.display_name || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-campaign-btn" className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                  <Plus size={16} className="mr-2" /> New Campaign
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)]" data-testid="campaign-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display">Create Campaign</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Name</label>
                    <Input data-testid="campaign-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                      placeholder="Campaign name" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Platform</label>
                    <Select value={form.platform} onValueChange={v => setForm({...form, platform: v})}>
                      <SelectTrigger data-testid="campaign-platform-select" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                        {['Meta', 'Google', 'TikTok'].map(p => <SelectItem key={p} value={p} className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Budget ($)</label>
                    <Input data-testid="campaign-budget-input" type="number" value={form.budget}
                      onChange={e => setForm({...form, budget: parseFloat(e.target.value) || 0})}
                      className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Objective</label>
                    <Select value={form.objective} onValueChange={v => setForm({...form, objective: v})}>
                      <SelectTrigger data-testid="campaign-objective-select" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                        {['Conversions', 'Traffic', 'Brand Awareness', 'Lead Gen'].map(o => (
                          <SelectItem key={o} value={o} className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Audience</label>
                    <Input data-testid="campaign-audience-input" value={form.audience}
                      onChange={e => setForm({...form, audience: e.target.value})}
                      placeholder="Target audience" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                  </div>
                  <Button data-testid="save-campaign-btn" onClick={handleCreateCampaign} className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                    Create Campaign
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" />
          </div>
        ) : !selectedClient ? (
          <div className="nx-metric-card rounded-lg text-center py-12">
            <Megaphone size={40} className="mx-auto mb-3 text-[var(--text-tertiary)]" />
            <p className="text-[var(--text-secondary)]">Select a client to manage campaigns</p>
          </div>
        ) : (
          <>
          {/* ═══ LEVEL 1: Platform Selector (sticky) ═══ */}
          <div className="sticky top-0 z-10 bg-[var(--canvas-secondary)] pb-3 -mx-1 px-1">
            <div className="flex gap-3 md:gap-4">
              <button onClick={() => setActivePlatform('meta')} data-testid="platform-meta"
                className={`flex-1 p-3 md:p-4 rounded-lg border-2 transition-all duration-200 ${activePlatform === 'meta' ? 'border-[#1877F2] bg-[#1877F2]/10' : 'border-[var(--border)] hover:border-[var(--accent-border)] bg-[var(--surface)]'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded flex items-center justify-center text-sm font-bold ${activePlatform === 'meta' ? 'bg-[#1877F2] text-white' : 'bg-[var(--surface-sunken)] text-[var(--text-tertiary)]'}`}>M</div>
                  <div className="text-left">
                    <p className={`text-sm font-bold ${activePlatform === 'meta' ? 'text-[#1877F2]' : 'text-[var(--text-secondary)]'}`}>Meta Ads</p>
                    <p className="text-[9px] text-[var(--text-tertiary)] font-mono">Andromeda-native</p>
                  </div>
                </div>
              </button>
              <button onClick={() => setActivePlatform('google')} data-testid="platform-google"
                className={`flex-1 p-3 md:p-4 rounded-lg border-2 transition-all duration-200 ${activePlatform === 'google' ? 'border-[#4285F4] bg-[#4285F4]/10' : 'border-[var(--border)] hover:border-[var(--accent-border)] bg-[var(--surface)]'}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded flex items-center justify-center text-sm font-bold ${activePlatform === 'google' ? 'bg-[#4285F4] text-white' : 'bg-[var(--surface-sunken)] text-[var(--text-tertiary)]'}`}>G</div>
                  <div className="text-left">
                    <p className={`text-sm font-bold ${activePlatform === 'google' ? 'text-[#4285F4]' : 'text-[var(--text-secondary)]'}`}>Google Ads</p>
                    <p className="text-[9px] text-[var(--text-tertiary)] font-mono">PMax-native</p>
                  </div>
                </div>
              </button>
            </div>
            {/* Context banner */}
            <div className="mt-2 px-3 py-1.5 rounded bg-[var(--surface-sunken)] border border-[var(--border)] flex items-center gap-2 text-[9px] font-mono text-[var(--text-tertiary)]">
              <span className={`w-1.5 h-1.5 rounded-full ${activePlatform === 'meta' ? 'bg-[#1877F2]' : 'bg-[#4285F4]'}`} />
              <span>{activePlatform === 'meta' ? 'Meta Business Manager' : 'Google Ads Account'}</span>
              <span className="text-[var(--text-secondary)]">|</span>
              <span>Last sync: just now</span>
            </div>
          </div>

          {/* ═══ LEVEL 2: Platform-specific sub-tabs ═══ */}
          <Tabs defaultValue="campaigns" className="space-y-4" key={activePlatform}>
            <TabsList className="bg-[var(--surface)] border border-[var(--border)] overflow-x-auto flex-nowrap">
              {activePlatform === 'meta' ? (
                <>
                  <TabsTrigger value="campaigns" className="data-[state=active]:bg-[#1877F2] data-[state=active]:text-white whitespace-nowrap">Campaigns</TabsTrigger>
                  <TabsTrigger value="performance" className="data-[state=active]:bg-[#1877F2] data-[state=active]:text-white whitespace-nowrap">Performance</TabsTrigger>
                  <TabsTrigger value="sku" className="data-[state=active]:bg-[#1877F2] data-[state=active]:text-white whitespace-nowrap">SKU Report</TabsTrigger>
                  <TabsTrigger value="hitl" className="data-[state=active]:bg-[#1877F2] data-[state=active]:text-white whitespace-nowrap">HITL Approvals</TabsTrigger>
                  <TabsTrigger value="andromeda" className="data-[state=active]:bg-[#1877F2] data-[state=active]:text-white whitespace-nowrap">Andromeda Health</TabsTrigger>
                  <TabsTrigger value="creative-portfolio" className="data-[state=active]:bg-[#1877F2] data-[state=active]:text-white whitespace-nowrap">Creative Portfolio</TabsTrigger>
                  <TabsTrigger value="approvals" className="data-[state=active]:bg-[#1877F2] data-[state=active]:text-white whitespace-nowrap">AI Citation</TabsTrigger>
                </>
              ) : (
                <>
                  <TabsTrigger value="campaigns" className="data-[state=active]:bg-[#4285F4] data-[state=active]:text-white whitespace-nowrap">Campaigns</TabsTrigger>
                  <TabsTrigger value="performance" className="data-[state=active]:bg-[#4285F4] data-[state=active]:text-white whitespace-nowrap">Performance</TabsTrigger>
                  <TabsTrigger value="pmax" className="data-[state=active]:bg-[#4285F4] data-[state=active]:text-white whitespace-nowrap">PMax Health</TabsTrigger>
                  <TabsTrigger value="assets" className="data-[state=active]:bg-[#4285F4] data-[state=active]:text-white whitespace-nowrap">Asset Groups</TabsTrigger>
                  <TabsTrigger value="shopping" className="data-[state=active]:bg-[#4285F4] data-[state=active]:text-white whitespace-nowrap">Shopping Feed</TabsTrigger>
                  <TabsTrigger value="aimax" className="data-[state=active]:bg-[#4285F4] data-[state=active]:text-white whitespace-nowrap">AI Max Intel</TabsTrigger>
                  <TabsTrigger value="g-approvals" className="data-[state=active]:bg-[#4285F4] data-[state=active]:text-white whitespace-nowrap">HITL Approvals</TabsTrigger>
                </>
              )}
            </TabsList>

            {/* Campaigns */}
            <TabsContent value="campaigns" className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Total Spend', value: `$${totalSpend.toLocaleString()}`, icon: CurrencyDollar, color: 'var(--accent)' },
                  { label: 'Conversions', value: totalConversions, icon: Target, color: 'var(--success)' },
                  { label: 'Avg ROAS', value: `${avgRoas}x`, icon: TrendUp, color: 'var(--warning)' },
                  { label: 'Campaigns', value: campaigns.length, icon: Megaphone, color: 'var(--accent)' },
                ].map(m => {
                  const Icon = m.icon;
                  return (
                    <div key={m.label} className="nx-metric-card rounded-lg" data-testid={`ads-metric-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
                      <Icon size={18} style={{ color: m.color }} weight="duotone" className="mb-2" />
                      <p className="text-2xl font-bold text-[var(--text-primary)] font-mono">{m.value}</p>
                      <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mt-1">{m.label}</p>
                    </div>
                  );
                })}
              </div>
              <div className="nx-metric-card rounded-lg" data-testid="campaigns-table">
                <table className="nx-table">
                  <thead>
                    <tr><th>Campaign</th><th>Platform</th><th>Status</th><th>Budget</th><th>Spend</th><th>CTR</th><th>ROAS</th><th>AI</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {campaigns.map(c => (
                      <tr key={c.id}>
                        <td className="!text-[var(--text-primary)] font-medium">{c.name}</td>
                        <td><span className="nx-tag" style={{ background: `${PLATFORM_COLORS[c.platform]}20`, color: PLATFORM_COLORS[c.platform] }}>{c.platform}</span></td>
                        <td><span className="flex items-center gap-2"><span className={`nx-status-dot ${c.status}`} />{c.status}</span></td>
                        <td className="font-mono">${c.budget?.toLocaleString()}</td>
                        <td className="font-mono">${c.spend?.toLocaleString()}</td>
                        <td className="font-mono">{c.ctr}%</td>
                        <td className={`font-mono font-bold ${c.roas >= 3 ? 'text-[var(--success)]' : c.roas >= 1.5 ? 'text-[var(--warning)]' : 'text-[var(--error)]'}`}>{c.roas}x</td>
                        <td className="text-center">
                          {(() => {
                            const ov = overlay?.overlays?.find(o => o.campaign_name === c.name);
                            if (!ov) return <span className="text-[var(--text-tertiary)]">—</span>;
                            if (ov.ai_cited) return <span className="w-2 h-2 rounded-full bg-[var(--success)] inline-block" title="AI Cited" />;
                            if (ov.citation_gap) return <Warning size={12} className="text-[var(--warning)] inline" weight="fill" title="Citation Gap: High ROAS but zero AI citations" />;
                            return <span className="w-2 h-2 rounded-full bg-[var(--text-tertiary)] inline-block" title="Not Cited" />;
                          })()}
                        </td>
                        <td>
                          <Button
                            data-testid={`toggle-campaign-${c.id}`}
                            size="sm" variant="ghost"
                            className={`text-xs ${c.status === 'active' ? 'text-[var(--warning)] hover:text-[var(--warning)]' : 'text-[var(--success)] hover:text-[var(--success)]'}`}
                            onClick={() => toggleCampaignStatus(c)}
                          >
                            {c.status === 'active' ? 'Pause' : 'Activate'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Performance */}
            <TabsContent value="performance" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 nx-metric-card rounded-lg" data-testid="performance-chart">
                  <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Daily Performance</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={performance}>
                        <defs>
                          <linearGradient id="gRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="var(--success)" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="var(--success)" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="gSpend2" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="var(--error)" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="var(--error)" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis dataKey="date" tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12 }} />
                        <Area type="monotone" dataKey="revenue" stroke="var(--success)" fill="url(#gRevenue)" strokeWidth={2} />
                        <Area type="monotone" dataKey="spend" stroke="var(--error)" fill="url(#gSpend2)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="nx-metric-card rounded-lg" data-testid="platform-breakdown">
                  <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Spend by Platform</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={platformData} dataKey="spend" nameKey="platform" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                          {platformData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2 mt-2">
                    {platformData.map((p, i) => (
                      <div key={p.platform} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i] }} />
                          <span className="text-[var(--text-secondary)]">{p.platform}</span>
                        </span>
                        <span className="font-mono text-[var(--text-primary)]">${p.spend.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* SKU Report */}
            <TabsContent value="sku" className="space-y-4">
              <div className="nx-metric-card rounded-lg" data-testid="sku-report-table">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">SKU-Level Performance (Meta Marketing API)</h3>
                <div className="overflow-x-auto">
                  <table className="nx-table">
                    <thead>
                      <tr><th>SKU</th><th>Product</th><th>Impressions</th><th>Clicks</th><th>Purchases</th><th>Revenue</th><th>Ad Spend</th><th>ROAS</th><th>ACoS</th></tr>
                    </thead>
                    <tbody>
                      {skuReport.map(s => (
                        <tr key={s.id}>
                          <td className="font-mono text-xs text-[var(--accent)]">{s.sku}</td>
                          <td className="!text-[var(--text-primary)]">{s.product_name}</td>
                          <td className="font-mono">{s.impressions.toLocaleString()}</td>
                          <td className="font-mono">{s.clicks.toLocaleString()}</td>
                          <td className="font-mono">{s.purchases}</td>
                          <td className="font-mono text-[var(--success)]">${s.revenue.toLocaleString()}</td>
                          <td className="font-mono">${s.ad_spend.toLocaleString()}</td>
                          <td className={`font-mono font-bold ${s.roas >= 3 ? 'text-[var(--success)]' : s.roas >= 1.5 ? 'text-[var(--warning)]' : 'text-[var(--error)]'}`}>{s.roas}x</td>
                          <td className="font-mono">{s.acos}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="nx-metric-card rounded-lg" data-testid="sku-roas-chart">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">ROAS by Product</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={skuReport}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="product_name" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} axisLine={false} />
                      <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }} axisLine={false} />
                      <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12 }} />
                      <Bar dataKey="roas" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="acos" fill="var(--warning)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </TabsContent>

            {/* HITL Workflow */}
            <TabsContent value="hitl" className="space-y-4">
              <div className="nx-metric-card rounded-md">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Agentic Approval Workflow — Human-in-the-Loop</h3>
                <div className="flex items-center gap-0 overflow-x-auto pb-4 pipeline-stack">
                  {[
                    { step: 1, label: 'Agent Monitors 24/7', desc: 'Drafts Strategic Directive with rationale + preview', color: '#1A56DB' },
                    { step: 2, label: 'Human Review Gate', desc: 'Recommendation + AI Rationale + Creative Preview', color: '#F59E0B' },
                    { step: 3, label: 'One-Click Approval', desc: 'Pushed live via Meta/Google Ads API', color: '#00D4AA' },
                    { step: 4, label: '72hr Re-Optimisation', desc: 'Before/after comparison report', color: '#06B6D4' },
                  ].map((s, i) => (
                    <div key={s.step} className="flex items-center">
                      <div className="pipeline-node min-w-[160px]" style={{ borderColor: `${s.color}40` }}>
                        <div className="flex items-center justify-center w-6 h-6 rounded-full mx-auto mb-1.5" style={{ background: `${s.color}20`, color: s.color }}>
                          <span className="text-xs font-bold font-mono">{s.step}</span>
                        </div>
                        <p className="text-[10px] text-[var(--text-primary)] font-medium">{s.label}</p>
                        <p className="text-[8px] text-[var(--text-tertiary)] mt-0.5">{s.desc}</p>
                      </div>
                      {i < 3 && <div className="pipeline-connector mx-1" />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Sample HITL Cards */}
              <div className="space-y-3">
                {[
                  { trigger: 'Ad Fatigue Detected', campaign: 'Meta — Conversions Q1', impact: '1.2x projected ROAS lift', confidence: 87, action: 'Replace creative with winning A/B variant' },
                  { trigger: 'Budget Migration Opportunity', campaign: 'Google — Brand Search', impact: '+42% ROAS if shifted to TikTok', confidence: 72, action: 'Reallocate 30% budget from Google to TikTok' },
                  { trigger: 'Audience Expansion Signal', campaign: 'TikTok — Tier 2 India', impact: '+35% engagement with Hindi variant', confidence: 91, action: 'Launch Hindi creative variant for Tier-2 audiences' },
                ].map((card, i) => (
                  <div key={i} className="hitl-card">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[9px] font-mono text-[var(--accent)] uppercase tracking-wider">Trigger: {card.trigger}</span>
                          <span className="text-[9px] font-mono text-[var(--text-tertiary)]">{card.campaign}</span>
                        </div>
                        <p className="text-xs text-[var(--text-primary)]">{card.action}</p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-[9px] font-mono text-[var(--success)]">Projected: {card.impact}</span>
                          <span className={`text-[9px] font-mono ${card.confidence >= 85 ? 'text-[var(--success)]' : 'text-[var(--warning)]'}`}>
                            Confidence: {card.confidence}% {card.confidence >= 90 ? '— Safe for Auto-Approve' : card.confidence >= 70 ? '— Manual Review' : ''}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button className="hitl-card approve-btn px-3 py-1.5 rounded text-[10px] font-medium" onClick={() => toast.success('Approved — pushing live')}>Approve</button>
                        <button className="hitl-card reject-btn px-3 py-1.5 rounded text-[10px]" onClick={() => toast.success('Rejected')}>Reject</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* ═══ ANDROMEDA AUDIT ═══ */}
            <TabsContent value="andromeda" className="space-y-4">
              {andromeda && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="nx-metric-card rounded-md text-center py-5">
                      <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest mb-2">Campaign Structure Score</p>
                      <p className="text-5xl font-black font-mono" style={{ color: andromeda.score >= 80 ? 'var(--success)' : andromeda.score >= 50 ? 'var(--warning)' : 'var(--error)' }}>{andromeda.score}</p>
                      <span className={`inline-block mt-2 px-3 py-1 text-[10px] font-mono rounded ${andromeda.status === 'optimised' ? 'bg-[var(--success-bg)] text-[var(--success)]' : andromeda.status === 'needs_consolidation' ? 'bg-[var(--warning-bg)] text-[var(--warning)]' : 'bg-[var(--error-bg)] text-[var(--error)]'}`}>
                        {andromeda.status === 'optimised' ? 'Andromeda-Optimised' : andromeda.status === 'needs_consolidation' ? 'Needs Consolidation' : 'Fighting Andromeda'}
                      </span>
                    </div>
                    <div className="md:col-span-2 nx-metric-card rounded-md">
                      <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Recommended Actions</h3>
                      <div className="space-y-2">
                        {andromeda.recommendations?.map((r, i) => (
                          <div key={i} className="flex items-start gap-3 p-2.5 rounded border border-[var(--border)]">
                            <Warning size={14} className={`mt-0.5 flex-shrink-0 ${r.severity === 'high' ? 'text-[var(--error)]' : 'text-[var(--warning)]'}`} weight="fill" />
                            <div className="flex-1"><p className="text-xs text-[var(--text-primary)]">{r.action}</p><p className="text-[9px] text-[var(--success)] font-mono mt-0.5">{r.impact}</p></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="nx-metric-card rounded-md">
                    <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Campaign Breakdown</h3>
                    <table className="w-full text-[10px] font-mono border-collapse">
                      <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]"><th className="text-left p-2">Campaign</th><th className="text-center p-2">Ad Sets</th><th className="text-center p-2">Creatives</th><th className="text-center p-2">Targeting</th><th className="text-center p-2">Learning</th></tr></thead>
                      <tbody>
                        {andromeda.campaigns?.map((c, i) => (
                          <tr key={i} className="border-b border-[var(--border-light)]">
                            <td className="p-2 text-[var(--text-primary)]">{c.name}</td>
                            <td className={`p-2 text-center ${c.ad_sets_flag ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>{c.ad_sets} {c.ad_sets_flag && '(>3)'}</td>
                            <td className={`p-2 text-center ${c.creatives_flag ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>{c.creatives} {c.creatives_flag && '(<15)'}</td>
                            <td className={`p-2 text-center ${c.targeting_flag ? 'text-[var(--warning)]' : 'text-[var(--text-secondary)]'}`}>{c.targeting.replace('_', ' ')}</td>
                            <td className="p-2 text-center">{c.learning_phase ? <Clock size={12} className="inline text-[var(--warning)]" /> : <CheckCircle size={12} className="inline text-[var(--success)]" weight="fill" />}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </TabsContent>

            {/* ═══ CREATIVE PORTFOLIO ═══ */}
            <TabsContent value="creative-portfolio" className="space-y-4">
              {portfolio && (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 nx-metric-card rounded-md">
                      <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Creative Health Radar</h3>
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={Object.entries(portfolio.radar || {}).map(([k, v]) => ({ metric: k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), value: v }))}>
                            <PolarGrid stroke="var(--border)" />
                            <PolarAngleAxis dataKey="metric" tick={{ fill: 'var(--text-secondary)', fontSize: 9 }} />
                            <PolarRadiusAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 8 }} domain={[0, 100]} />
                            <Radar dataKey="value" stroke="var(--accent)" fill="var(--accent)" fillOpacity={0.2} strokeWidth={2} />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="nx-metric-card rounded-md">
                      <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Portfolio Summary</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between"><span className="text-[10px] text-[var(--text-secondary)]">Total Creatives</span><span className="text-sm font-bold text-[var(--text-primary)] font-mono">{portfolio.total}</span></div>
                        <div className="flex justify-between"><span className="text-[10px] text-[var(--text-secondary)]">Fatigued</span><span className="text-sm font-bold text-[var(--error)] font-mono">{portfolio.fatigued_count}</span></div>
                        {Object.entries(portfolio.radar || {}).map(([k, v]) => (
                          <div key={k} className="flex items-center gap-2"><span className="text-[9px] text-[var(--text-tertiary)] w-20 capitalize">{k.replace(/_/g, ' ')}</span>
                            <div className="flex-1 h-1.5 bg-[var(--surface-sunken)] rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${v}%`, background: v >= 70 ? 'var(--success)' : v >= 40 ? 'var(--warning)' : 'var(--error)' }} /></div>
                            <span className="text-[9px] font-mono text-[var(--text-primary)] w-8 text-right">{v}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="nx-metric-card rounded-md">
                    <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Active Creatives</h3>
                    <table className="w-full text-[10px] font-mono border-collapse">
                      <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]"><th className="text-left p-2">Creative</th><th className="p-2">Format</th><th className="text-right p-2">Hook Score</th><th className="text-right p-2">Age</th><th className="p-2">Fatigue</th><th className="p-2">Topic</th></tr></thead>
                      <tbody>
                        {portfolio.creatives?.map(c => (
                          <tr key={c.id} className="border-b border-[var(--border-light)]">
                            <td className="p-2 text-[var(--text-primary)]">{c.name}</td>
                            <td className="p-2"><span className="nx-tag nx-tag-blue text-[8px]">{c.format}</span></td>
                            <td className={`p-2 text-right ${c.hook_score > 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>{c.hook_score > 0 ? '+' : ''}{c.hook_score}%</td>
                            <td className={`p-2 text-right ${c.age_flag ? 'text-[var(--warning)]' : 'text-[var(--text-secondary)]'}`}>{c.age_days}d</td>
                            <td className="p-2 text-center"><span className={`w-2 h-2 rounded-full inline-block ${c.fatigue === 'green' ? 'bg-[var(--success)]' : c.fatigue === 'amber' ? 'bg-[var(--warning)]' : 'bg-[var(--error)]'}`} /></td>
                            <td className="p-2"><span className="nx-tag nx-tag-gray text-[8px]">{c.topic}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </TabsContent>

            {/* ═══ APPROVALS (HITL Bid & Budget) ═══ */}
            <TabsContent value="approvals" className="space-y-4">
              {approvals && (
                <>
                  <div className="space-y-3">
                    {approvals.proposals?.map(p => (
                      <div key={p.id} className="hitl-card">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[9px] font-mono text-[var(--accent)] uppercase tracking-wider">{p.type.replace(/_/g, ' ')}</span>
                              <span className="text-[9px] font-mono text-[var(--success)]">{p.impact}</span>
                            </div>
                            <p className="text-xs text-[var(--text-primary)] leading-relaxed">{p.detail}</p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0 ml-4">
                            {p.type === 'budget_shift' && (
                              <>
                                <button className="px-2.5 py-1.5 rounded text-[10px] font-medium bg-[var(--success)] text-[var(--text-primary)]" onClick={() => toast.success('Budget shift approved')}>Approve</button>
                                <button className="px-2.5 py-1.5 rounded text-[10px] border border-[var(--border)] text-[var(--text-secondary)]" onClick={() => toast.success('Modify')}>Modify</button>
                                <button className="px-2.5 py-1.5 rounded text-[10px] border border-[var(--error-border)] text-[var(--error)]" onClick={() => toast.success('Rejected')}>Reject</button>
                              </>
                            )}
                            {p.type === 'consolidation' && (
                              <>
                                <button className="px-2.5 py-1.5 rounded text-[10px] font-medium bg-[var(--success)] text-[var(--text-primary)]" onClick={() => toast.success('Merge approved')}>Approve Merge</button>
                                <button className="px-2.5 py-1.5 rounded text-[10px] border border-[var(--border)] text-[var(--text-secondary)]" onClick={() => toast.success('Kept separate')}>Keep Separate</button>
                              </>
                            )}
                            {p.type === 'creative_retirement' && (
                              <>
                                <button className="px-2.5 py-1.5 rounded text-[10px] font-medium bg-[var(--error)] text-white" onClick={() => toast.success('Creative archived')}><Archive size={10} className="inline mr-1" />Archive</button>
                                <button className="px-2.5 py-1.5 rounded text-[10px] border border-[var(--border)] text-[var(--text-secondary)]" onClick={() => toast.success('Snoozed 7 days')}><Clock size={10} className="inline mr-1" />Snooze 7d</button>
                                <button className="px-2.5 py-1.5 rounded text-[10px] border border-[var(--border)] text-[var(--text-secondary)]">Keep</button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {approvals.history?.length > 0 && (
                    <div className="nx-metric-card rounded-md">
                      <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Approval History — Projected vs Actual</h3>
                      <table className="w-full text-[10px] font-mono border-collapse">
                        <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]"><th className="text-left p-2">Type</th><th className="p-2">Action</th><th className="p-2">Date</th><th className="p-2">Projected</th><th className="p-2">Actual (14d)</th></tr></thead>
                        <tbody>
                          {approvals.history.map(h => (
                            <tr key={h.id} className="border-b border-[var(--border-light)]">
                              <td className="p-2 text-[var(--text-primary)] capitalize">{h.type.replace(/_/g, ' ')}</td>
                              <td className="p-2"><span className="nx-tag nx-tag-green text-[8px]">{h.action}</span></td>
                              <td className="p-2 text-[var(--text-secondary)]">{new Date(h.date).toLocaleDateString()}</td>
                              <td className="p-2 text-[var(--warning)]">{h.projected}</td>
                              <td className="p-2 text-[var(--success)]">{h.actual}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* ═══ PMAX AUDIT ═══ */}
            <TabsContent value="pmax" className="space-y-4">
              {pmaxAudit && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="nx-metric-card rounded-md text-center py-5">
                      <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest mb-2">PMax Readiness Score</p>
                      <p className="text-5xl font-black font-mono" style={{ color: pmaxAudit.score >= 80 ? 'var(--success)' : pmaxAudit.score >= 50 ? 'var(--warning)' : 'var(--error)' }}>{pmaxAudit.score}</p>
                      <span className={`inline-block mt-2 px-3 py-1 text-[10px] font-mono rounded ${pmaxAudit.score >= 80 ? 'bg-[var(--success-bg)] text-[var(--success)]' : pmaxAudit.score >= 50 ? 'bg-[var(--warning-bg)] text-[var(--warning)]' : 'bg-[var(--error-bg)] text-[var(--error)]'}`}>
                        {pmaxAudit.status === 'optimised' ? 'PMax-Optimised' : pmaxAudit.status === 'needs_work' ? 'Needs Work' : 'Underperforming'}
                      </span>
                    </div>
                    <div className="md:col-span-2 nx-metric-card rounded-md">
                      <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Fixes by Impact</h3>
                      <div className="space-y-2">{pmaxAudit.recommendations?.map((r, i) => (
                        <div key={i} className="flex items-start gap-3 p-2.5 rounded border border-[var(--border)]">
                          <Warning size={14} className={`mt-0.5 ${r.severity === 'high' ? 'text-[var(--error)]' : 'text-[var(--warning)]'}`} weight="fill" />
                          <div className="flex-1"><p className="text-xs text-[var(--text-primary)]">{r.action}</p><p className="text-[9px] text-[var(--success)] font-mono mt-0.5">{r.impact}</p></div>
                        </div>
                      ))}</div>
                    </div>
                  </div>
                  <div className="nx-metric-card rounded-md">
                    <h3 className="text-sm font-bold text-[var(--text-primary)] mb-3">PMax Campaigns</h3>
                    <table className="w-full text-[10px] font-mono border-collapse">
                      <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]"><th className="text-left p-2">Campaign</th><th className="p-2">Assets</th><th className="p-2">Shopping Conflict</th><th className="p-2">Goal</th><th className="p-2">ROAS Align</th></tr></thead>
                      <tbody>{pmaxAudit.campaigns?.map((c, i) => (
                        <tr key={i} className="border-b border-[var(--border-light)]">
                          <td className="p-2 text-[var(--text-primary)]">{c.name}</td>
                          <td className={`p-2 text-center ${c.assets_complete ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>{c.assets_complete ? <CheckCircle size={12} weight="fill" className="inline" /> : `H:${c.assets.headlines} D:${c.assets.descriptions} I:${c.assets.images} V:${c.assets.videos}`}</td>
                          <td className="p-2 text-center">{c.shopping_conflict ? <Warning size={12} className="text-[var(--error)] inline" weight="fill" /> : <CheckCircle size={12} className="text-[var(--success)] inline" weight="fill" />}</td>
                          <td className={`p-2 text-center ${c.goal_quality === 'high' ? 'text-[var(--success)]' : 'text-[var(--warning)]'}`}>{c.conversion_goal}</td>
                          <td className="p-2 text-center">{c.roas_aligned ? <CheckCircle size={12} className="text-[var(--success)] inline" weight="fill" /> : <span className="text-[var(--warning)]">T:{c.target_roas}x A:{c.actual_roas}x</span>}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </>
              )}
            </TabsContent>

            {/* ═══ ASSET GROUPS ═══ */}
            <TabsContent value="assets" className="space-y-4">
              {assetGrps?.groups?.map(g => (
                <div key={g.name} className="nx-metric-card rounded-md">
                  <div className="flex items-center justify-between mb-3">
                    <div><h4 className="text-xs font-bold text-[var(--text-primary)]">{g.name}</h4><p className="text-[9px] text-[var(--text-tertiary)]">{g.campaign}</p></div>
                    {g.gaps.length > 0 && <span className="text-[9px] bg-[var(--warning-bg)] text-[var(--warning)] px-2 py-0.5 rounded font-mono">Gaps: {g.gaps.join(', ')}</span>}
                  </div>
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {['headlines', 'descriptions', 'images', 'videos'].map(type => {
                      const min = { headlines: 5, descriptions: 5, images: 5, videos: 2 }[type];
                      const count = g.assets[type]?.length || 0;
                      return (
                        <div key={type} className={`p-2 rounded border text-center ${count >= min ? 'border-[var(--success-border)]' : 'border-[var(--warning-border)] bg-[var(--warning-bg)]'}`}>
                          <p className="text-[8px] text-[var(--text-tertiary)] capitalize">{type}</p>
                          <p className={`text-lg font-bold font-mono ${count >= min ? 'text-[var(--text-primary)]' : 'text-[var(--warning)]'}`}>{count}/{min}</p>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {g.assets.headlines?.slice(0, 5).map((a, i) => (
                      <span key={i} className={`px-1.5 py-0.5 text-[8px] rounded font-mono ${a.performance === 'best' ? 'bg-[var(--success-bg)] text-[var(--success)]' : a.performance === 'good' ? 'bg-[var(--accent-light)] text-[var(--accent-hover)]' : a.performance === 'low' ? 'bg-[var(--error-bg)] text-[var(--error)]' : 'bg-[var(--surface-sunken)] text-[var(--text-tertiary)]'}`}>{a.text} ({a.performance})</span>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>

            {/* ═══ SHOPPING FEED ═══ */}
            <TabsContent value="shopping" className="space-y-4">
              {shopFeed && (
                <div className="nx-metric-card rounded-md">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display text-sm font-bold text-[var(--text-primary)]">Shopping Feed Health: {shopFeed.feed_health}%</h3>
                  </div>
                  <table className="w-full text-[10px] font-mono border-collapse">
                    <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]"><th className="text-left p-2">Product</th><th className="p-2">Title Score</th><th className="p-2">Image</th><th className="p-2">Missing</th><th className="p-2">Hindi Feed</th><th className="p-2">Imp Share</th></tr></thead>
                    <tbody>{shopFeed.products?.map(p => (
                      <tr key={p.sku} className="border-b border-[var(--border-light)]">
                        <td className="p-2"><span className="text-[var(--text-primary)]">{p.title}</span><br/><span className="text-[var(--text-tertiary)]">{p.sku}</span></td>
                        <td className={`p-2 text-center ${p.title_score >= 70 ? 'text-[var(--success)]' : 'text-[var(--warning)]'}`}>{p.title_score}%</td>
                        <td className="p-2 text-center"><span className={`nx-tag text-[8px] ${p.image_quality === 'good' ? 'nx-tag-green' : 'nx-tag-yellow'}`}>{p.image_quality}</span></td>
                        <td className="p-2 text-center">{p.missing_fields.length > 0 ? <span className="text-[var(--error)]">{p.missing_fields.join(', ')}</span> : <CheckCircle size={12} className="text-[var(--success)] inline" weight="fill" />}</td>
                        <td className="p-2 text-center">{p.hindi_feed ? <CheckCircle size={12} className="text-[var(--success)] inline" weight="fill" /> : <span className="text-[var(--warning)]">Missing</span>}</td>
                        <td className={`p-2 text-center ${p.impression_share >= 60 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>{p.impression_share}%</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* ═══ AI MAX SEARCH ═══ */}
            <TabsContent value="aimax" className="space-y-4">
              {aiMax && (
                <>
                  <div className="nx-metric-card rounded-md">
                    <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">AI Max Search Themes</h3>
                    <div className="flex items-center gap-4 mb-3">
                      <div><p className="text-3xl font-black text-[var(--accent)] font-mono">{aiMax.theme_count}</p><p className="text-[9px] text-[var(--text-tertiary)]">Your themes</p></div>
                      <div className="text-[var(--text-tertiary)]">vs</div>
                      <div><p className="text-3xl font-black text-[var(--text-secondary)] font-mono">{aiMax.competitor_estimate}</p><p className="text-[9px] text-[var(--text-tertiary)]">Top competitor (est.)</p></div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-3">{aiMax.themes?.map(t => <span key={t} className="nx-tag nx-tag-blue text-[8px]">{t}</span>)}</div>
                    <p className="text-[8px] text-[var(--text-secondary)] italic">AI Max themes auto-expand when landing pages have deeper Answer-First content</p>
                  </div>
                  {aiMax.geo_fixes?.length > 0 && (
                    <div className="nx-metric-card rounded-md border-[var(--accent-border)]">
                      <h4 className="text-xs font-bold text-[var(--text-primary)] mb-2">Answer-First Lift Tracker</h4>
                      {aiMax.geo_fixes.map((f, i) => (
                        <div key={i} className="p-2.5 rounded border border-[var(--border)] flex items-center gap-3">
                          <Gauge size={16} className="text-[var(--accent)]" />
                          <div className="flex-1">
                            <p className="text-[10px] text-[var(--text-primary)]">GEO Agent fix deployed to <span className="text-[var(--accent)]">{f.url}</span> on {f.date}</p>
                            <p className="text-[9px] text-[var(--text-tertiary)]">AI Max themes before: {f.themes_before} — Check again in 7 days</p>
                          </div>
                          <span className="nx-tag nx-tag-yellow text-[8px]">{f.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {qsSom?.has_data && (
                    <div className="nx-metric-card rounded-md border-[var(--success-border)]">
                      <h4 className="text-xs font-bold text-[var(--text-primary)] mb-2">Quality Score + SoM Correlation</h4>
                      <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{qsSom.insight}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="text-center"><p className="text-xl font-bold text-[var(--success)] font-mono">{qsSom.cited_avg_qs}</p><p className="text-[8px] text-[var(--text-tertiary)]">Cited Avg QS</p></div>
                        <div className="text-center"><p className="text-xl font-bold text-[var(--error)] font-mono">{qsSom.uncited_avg_qs}</p><p className="text-[8px] text-[var(--text-tertiary)]">Uncited Avg QS</p></div>
                        <div className="text-center"><p className="text-xl font-bold text-[var(--warning)] font-mono">-{qsSom.estimated_cpc_reduction}%</p><p className="text-[8px] text-[var(--text-tertiary)]">Est. CPC Reduction</p></div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* ═══ GOOGLE HITL APPROVALS ═══ */}
            <TabsContent value="g-approvals" className="space-y-3">
              {gApprovals?.proposals?.map(p => (
                <div key={p.id} className="hitl-card">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[9px] font-mono text-[var(--accent)] uppercase tracking-wider">{p.type.replace(/_/g, ' ')}</span>
                        <span className="text-[9px] font-mono text-[var(--success)]">{p.impact}</span>
                      </div>
                      <p className="text-xs text-[var(--text-primary)] leading-relaxed">{p.detail}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-4">
                      <button className="px-2.5 py-1.5 rounded text-[10px] font-medium bg-[var(--success)] text-[var(--text-primary)]" onClick={() => toast.success('Approved')}>Approve</button>
                      <button className="px-2.5 py-1.5 rounded text-[10px] border border-[var(--border)] text-[var(--text-secondary)]" onClick={() => toast.success('Modified')}>Modify</button>
                      <button className="px-2.5 py-1.5 rounded text-[10px] border border-[var(--error-border)] text-[var(--error)]" onClick={() => toast.success('Rejected')}>Reject</button>
                    </div>
                  </div>
                </div>
              ))}
            </TabsContent>
          </Tabs>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
